import { Component, computed, DestroyRef, effect, ElementRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ToastService } from '../../ui';
import { SessionService } from '../../core/session/session.service';
import { Contract, ContractRevision, ContractsService } from './contracts.service';
import { RevisionListComponent } from './revision-list.component';
import { getCurrency } from '../../shared/utils/currency.util';
import { moveStartDateBounds } from '../../shared/utils/contract-change-date.util';
import { currentOrLatestRevision } from './current-revision.util';
import {
  AddExtensionDialogComponent, AddExtensionDialogData,
  RemoveExtensionDialogComponent, RemoveExtensionDialogData,
  ChangePlanDialogComponent, ChangePlanDialogData,
  ChangeToBasicDialogComponent, ChangeToBasicDialogData,
  ChangeDiscountDialogComponent, ChangeDiscountDialogData,
  EndContractDialogComponent, EndContractDialogData,
  MoveStartDateDialogComponent, MoveStartDateDialogData,
  ExtendFreeTrialDialogComponent, ExtendFreeTrialDialogData,
} from './dialogs';
import { detectTrial, detectTrialFromContract, MAX_TRIAL_EXTENSIONS, todayIso } from './trial-detection.util';
import {
  HlmBadgeComponent, HlmBtnDirective, HlmCheckboxComponent, HlmIconComponent,
  HlmSpinnerComponent, HLM_MENU,
} from '../../ui';
import { environment } from '../../../environments/environment';
interface QuickNumber { value: string; type: string; }
interface QuickAccount {
  userAccountId: number;
  phoneServiceId: number;
  /** "LastName, FirstName" for the header, per the EB-3859 design. */
  userName: string;
  /** false when the related user account is deactivated (EB-3858). */
  active: boolean;
  numbers: QuickNumber[];
}

/**
 * A user account is active for "aktiv"/"active"; any other non-empty status
 * (e.g. "deaktiv"/"deactivated") is inactive. Unknown/empty → active (never a
 * false "inactive" badge). Mirrors user-account-info + entity-selector.
 */
function isUserAccountActive(status: string | null | undefined): boolean {
  if (!status) return true;
  return status === 'aktiv' || status === 'active';
}

@Component({
  selector: 'cmt-contract-panel',
  standalone: true,
  imports: [
    DatePipe, RevisionListComponent,
    HlmBadgeComponent, HlmBtnDirective, HlmCheckboxComponent, HlmIconComponent,
    HlmSpinnerComponent, ...HLM_MENU,
  ],
  templateUrl: './contract-panel.component.html',
})
export class ContractPanelComponent {
  private readonly contractsService = inject(ContractsService);
  readonly session = inject(SessionService);
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly el = inject(ElementRef<HTMLElement>);
  private hasScrolled = false;

  readonly contract = input.required<Contract>();
  /** If set and matches this contract's id, the panel auto-expands and scrolls into view. */
  readonly highlightContractId = input<number | null>(null);
  readonly contractsChanged = output<void>();

  readonly expanded = signal(false);
  readonly revisions = signal<ContractRevision[]>([]);
  readonly revisionsLoaded = signal(false);
  /**
   * "We tried and failed" — a third state, distinct from both "loaded" and "still loading".
   *
   * Without it the load could not settle: `loadRevisionsIfNeeded` guards on
   * `revisionsLoaded() || loadingRevisions()`, the auto-expand effect calls it, and the
   * effect re-runs when those very signals change — so a failed load left both false and
   * the effect immediately retried. Measured: one errored request produced six more
   * in-flight (PR #42 review, roundup #3, found while covering it).
   *
   * It also makes the failure legible to the template: actions whose input is the revision
   * list must not be offered when that list is unknown rather than merely empty.
   */
  readonly revisionsLoadFailed = signal(false);
  readonly loadingRevisions = signal(false);
  readonly showPastChanges = signal(true);
  readonly sortNewest = signal(true);
  readonly featureNames = signal<Record<string, string>>({});
  readonly extensionNames = signal<Record<string, string>>({});
  readonly upgradeAvailable = signal(false);
  readonly quickAccounts = signal<QuickAccount[]>([]);
  readonly accountsLoading = signal(false);
  private accountsLoaded = false;

  // Header UA/PS summary (EB-3858/EB-3859). The block is only shown for a
  // single-account contract; multi-account contracts show a compact hint
  // instead (per the agreed design).
  readonly singleAccount = computed(() =>
    this.quickAccounts().length === 1 ? this.quickAccounts()[0] : null,
  );
  readonly isMultiAccount = computed(() => this.quickAccounts().length > 1);
  /** True when any related user account is deactivated → header "inactive" badge. */
  readonly relatedInactive = computed(() => this.quickAccounts().some(a => !a.active));

  /** Which id was just copied ('ua' | 'ps'), for the transient check icon. */
  readonly copiedField = signal<'ua' | 'ps' | null>(null);
  private copyTimeout: ReturnType<typeof setTimeout> | undefined;

  readonly currency = computed(() => {
    const brandId = this.session.brandId();
    return getCurrency(brandId ?? 1);
  });

  readonly activeRevisionIndex = computed(() => {
    const revs = this.revisions();
    const today = todayIso();
    return revs.findIndex(r => r.startDate <= today && (r.endDate === null || r.endDate >= today));
  });

  readonly filteredRevisions = computed(() => {
    let revs = this.revisions();
    if (!this.showPastChanges()) {
      const today = todayIso();
      revs = revs.filter(r => r.endDate === null || r.endDate >= today);
    }
    const sorted = [...revs].sort((a, b) => a.startDate.localeCompare(b.startDate));
    return this.sortNewest() ? sorted.reverse() : sorted;
  });

  readonly filteredActiveIndex = computed(() => {
    const filtered = this.filteredRevisions();
    const today = todayIso();
    return filtered.findIndex(r => r.startDate <= today && (r.endDate === null || r.endDate >= today));
  });

  /**
   * EB-3922: free-trial detection. Once the revisions are loaded, use them (they
   * carry the precise extension count for the 3x cap). Before that, derive the
   * badge from the contract DTO's own `plan.level` + `endDate`, so the
   * always-visible header badge is correct on first paint — otherwise it would
   * flash the generic "Deactivated" (a bounded trial is always
   * `contract.deactivated`) until the lazily-loaded revisions arrive. */
  readonly trialInfo = computed(() =>
    this.revisionsLoaded()
      ? detectTrial(this.revisions())
      : detectTrialFromContract(this.contract()),
  );

  /** Cap exposed to the template for the disabled-state hint. */
  readonly maxTrialExtensions = MAX_TRIAL_EXTENSIONS;

  /** True when the trial has reached the 3x extension cap. */
  readonly trialCapReached = computed(
    () => this.trialInfo().trialExtensionCount >= MAX_TRIAL_EXTENSIONS,
  );

  constructor() {
    effect(() => {
      // Contract.id arrives from the API as a string at runtime despite its TS
      // `number` type — compare as strings, same defensive pattern used
      // elsewhere in this feature (see contracts.component.ts:67).
      //
      // Expansion policy:
      //   - When a specific contract was requested (highlightContractId set),
      //     expand ONLY that contract — don't also auto-open the main contract,
      //     since expanding two panels at once causes async revision loads to
      //     shift the layout mid-scroll.
      //   - Otherwise, fall back to the default of auto-expanding the main
      //     contract on page landing.
      const highlight = this.highlightContractId();
      const hasHighlight = highlight !== null && highlight !== undefined;
      const shouldExpand = hasHighlight
        ? this.isHighlighted()
        : this.contract().mainContract;
      if (shouldExpand) {
        this.expanded.set(true);
        this.loadRevisionsIfNeeded();
      }
    });

    // Scroll this panel into view when it's the highlighted contract — used by
    // the redirect-to-contracts flow from the Account Info page.
    //
    // Gated on revisionsLoaded so we scroll *after* this panel's body has
    // rendered — by then sibling panels (the main contract, which auto-loads
    // its revisions in parallel) have typically finished too, so layout is
    // stable and smooth scroll can't get re-targeted by a sudden height
    // change in a panel above us. The hasScrolled flag keeps it one-shot.
    effect(() => {
      if (!this.isHighlighted() || !this.revisionsLoaded() || this.hasScrolled) return;
      this.hasScrolled = true;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.el.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }));
    });

    // EB-3858/EB-3859: eagerly load the related UA/PS so the header summary
    // (name, UA/PS ids, inactive badge) is visible on the collapsed row without
    // opening the Accounts & Numbers menu. Reading contract() both registers the
    // dependency and guarantees the required input is set; loadQuickAccounts is
    // idempotent (accountsLoaded guard) so this runs once per panel.
    effect(() => {
      this.contract();
      this.loadQuickAccounts();
    });
  }

  private isHighlighted(): boolean {
    const highlight = this.highlightContractId();
    if (highlight === null || highlight === undefined) return false;
    return String(this.contract().id) === String(highlight);
  }

  toggleExpand(): void {
    this.expanded.update(v => !v);
    // Re-expanding clears a previous failure, so collapse/expand is a way back. Without
    // this the guard in loadRevisionsIfNeeded() early-returns forever and a single 503
    // wedges the panel for the life of the instance (PR #42 re-review defect 1).
    if (this.expanded()) {
      this.revisionsLoadFailed.set(false);
      this.loadRevisionsIfNeeded();
    }
  }

  /** Explicit retry from the error row. */
  retryRevisionLoad(): void {
    this.revisionsLoadFailed.set(false);
    this.loadRevisionsIfNeeded();
  }

  toggleShowPastChanges(): void { this.showPastChanges.update(v => !v); }
  toggleSortOrder(): void { this.sortNewest.update(v => !v); }

  /** Tooltip listing all of an account's numbers (EB-3859 follow-up: hover shows all). */
  numbersTitle(acc: QuickAccount): string {
    return acc.numbers.map(n => (n.type ? `${n.value} (${n.type})` : n.value)).join('\n');
  }

  /** Copy a UA/PS id to the clipboard; shows a transient check icon (EB-3859). */
  copyId(event: Event, field: 'ua' | 'ps', id: number): void {
    event.stopPropagation();
    navigator.clipboard?.writeText(String(id));
    clearTimeout(this.copyTimeout);
    this.copiedField.set(field);
    this.copyTimeout = setTimeout(() => this.copiedField.set(null), 1500);
  }

  private get invoiceDay(): number { return this.session.customer()?.invoiceDay ?? 1; }
  private get activeRevision(): ContractRevision | null {
    const idx = this.activeRevisionIndex();
    return idx >= 0 ? this.revisions()[idx] : null;
  }

  openAddExtension(): void {
    const rev = this.activeRevision;
    const data: AddExtensionDialogData = {
      contractId: this.contract().id, customerId: this.session.customerId()!,
      invoiceDay: this.invoiceDay,
      // Price list + tariff are re-resolved inside the dialog from the revision valid on the
      // selected start date (EB-4210); these two only seed the header.
      tariff: rev?.tariff ?? '', currentPlan: rev?.productName ?? 'Unknown',
      priceListName: rev?.priceListName ?? '', currency: this.currency(),
    };
    this.dialog.open(AddExtensionDialogComponent, { data, width: '600px' })
      .afterClosed().subscribe(r => r && this.reloadAfterChange());
  }

  openRemoveExtension(): void {
    const rev = this.activeRevision;
    const data: RemoveExtensionDialogData = {
      contractId: this.contract().id, customerId: this.session.customerId()!,
      invoiceDay: this.invoiceDay, currentPlan: rev?.productName ?? 'Unknown',
      currency: this.currency(),
    };
    this.dialog.open(RemoveExtensionDialogComponent, { data, width: '500px' })
      .afterClosed().subscribe(r => r && this.reloadAfterChange());
  }

  openChangePlan(): void {
    const rev = this.activeRevision;
    const data: ChangePlanDialogData = {
      contractId: this.contract().id, customerId: this.session.customerId()!,
      invoiceDay: this.invoiceDay,
      // Old CMT derives the earliest change date from the contract itself plus the
      // last invoiced period (checkDatesForContractChanges) — EB-4174 follow-up.
      contractStartDate: this.contract().startDate,
      reactivatedStartDate: this.contract().reactivatedStartDate ?? null,
      planLevel: this.contract().plan?.level ?? null,
      currentPlanName: rev?.productName ?? '',
      currentPlanPrice: rev?.price ?? 0, priceListName: rev?.priceListName ?? '',
      currentPriceListId: rev?.priceListId ?? null,
      currency: this.currency(),
    };
    this.dialog.open(ChangePlanDialogComponent, { data, width: '500px' })
      .afterClosed().subscribe(r => r && this.reloadAfterChange());
  }

  openChangeToBasic(): void {
    // Falls back to the latest revision when none is running today, the same way the
    // Cancel-Customer path does — the two used to disagree, and the panel's `?? 0` turned
    // "unknown price list" into "price list #0", which no plan matches (PR #42 review H4).
    const rev = currentOrLatestRevision(this.revisions());
    if (!rev?.priceListId) {
      this.toast.open(
        'Cannot determine the price list for this contract — reload it and try again.',
        'OK',
        { duration: 5000, tone: 'error' },
      );
      return;
    }
    const data: ChangeToBasicDialogData = {
      contractId: this.contract().id,
      customerId: this.session.customerId()!,
      invoiceDay: this.invoiceDay,
      contractStartDate: this.contract().startDate,
      reactivatedStartDate: this.contract().reactivatedStartDate ?? null,
      planLevel: this.contract().plan?.level ?? null,
      currentPlanName: rev.productName,
      priceListId: rev.priceListId,
      priceListName: rev.priceListName ?? '',
      currency: this.currency(),
    };
    this.dialog.open(ChangeToBasicDialogComponent, { data, width: '460px' })
      .afterClosed().subscribe(r => r && this.reloadAfterChange());
  }

  openChangeDiscountPlan(): void {
    const rev = this.activeRevision;
    const data: ChangeDiscountDialogData = {
      contractId: this.contract().id, customerId: this.session.customerId()!,
      invoiceDay: this.invoiceDay, mode: 'plan', monthlyFee: rev?.price,
      currentPlanName: rev?.productName ?? 'Unknown', currency: this.currency(),
    };
    this.dialog.open(ChangeDiscountDialogComponent, { data, width: '500px' })
      .afterClosed().subscribe(r => r && this.reloadAfterChange());
  }

  openChangeDiscountExtension(): void {
    const rev = this.activeRevision;
    const data: ChangeDiscountDialogData = {
      contractId: this.contract().id, customerId: this.session.customerId()!,
      invoiceDay: this.invoiceDay, mode: 'extension',
      currentPlanName: rev?.productName ?? 'Unknown', currency: this.currency(),
    };
    this.dialog.open(ChangeDiscountDialogComponent, { data, width: '500px' })
      .afterClosed().subscribe(r => r && this.reloadAfterChange());
  }

  openEndContract(): void {
    const rev = this.activeRevision;
    const data: EndContractDialogData = {
      contractId: this.contract().id, invoiceDay: this.invoiceDay,
      currentPlan: rev?.productName ?? 'Unknown', startDate: this.contract().startDate,
    };
    this.dialog.open(EndContractDialogComponent, { data, width: '450px' })
      .afterClosed().subscribe(r => r && this.reloadAfterChange());
  }

  openMoveStartDate(): void {
    const c = this.contract();
    const rev = this.activeRevision;
    // Old CMT bounds this by today .. reference + 1/2 months, not by the contract's own
    // start and end — see moveStartDateBounds (EB-4170 review R2-2b).
    const bounds = moveStartDateBounds(c.startDate, this.revisions());
    const data: MoveStartDateDialogData = {
      contractId: c.id, invoiceDay: this.invoiceDay,
      minDate: bounds.minDate, maxDate: bounds.maxDate,
      currentPlan: rev?.productName ?? 'Unknown', currentStartDate: c.startDate,
    };
    this.dialog.open(MoveStartDateDialogComponent, { data, width: '450px' })
      .afterClosed().subscribe(r => r && this.reloadAfterChange());
  }

  openExtendFreeTrial(): void {
    const info = this.trialInfo();
    if (!info.isFreeTrial || this.trialCapReached()) return;
    const data: ExtendFreeTrialDialogData = {
      contractId: this.contract().id,
      currentEnd: info.currentEnd,
      trialState: info.trialState,
      trialExtensionCount: info.trialExtensionCount,
    };
    this.dialog.open(ExtendFreeTrialDialogComponent, { data, width: '440px' })
      .afterClosed().subscribe(r => r && this.reloadAfterChange());
  }

  openUpgradeExtension(): void {
    this.toast.open('Upgrade not yet implemented', 'OK', { duration: 3000, tone: 'default' });
  }

  reactivateContract(): void {
    this.toast.open(
      `Reactivation of contract ${this.contract().id} — please use SMT (Manage Services) when available`,
      'OK', { duration: 5000, tone: 'default' },
    );
  }

  loadQuickAccounts(): void {
    if (this.accountsLoaded || this.accountsLoading()) return;
    this.accountsLoading.set(true);
    this.accountsLoaded = true;
    this.http.get<any[]>(`${environment.phoneServiceUrl}/phoneservices`, {
      params: { contractId: this.contract().id.toString() },
    }).pipe(
      switchMap((services) => {
        if (services.length === 0) return of([]);
        return forkJoin(services.map(svc =>
          forkJoin({
            user: this.http.get<any>(`${environment.userAccountUrl}/useraccounts/${svc.userAccountId}`).pipe(
              catchError(() => of({ firstName: '', lastName: '', status: '' })),
            ),
            numbers: this.http.get<any[]>(`${environment.phoneServiceUrl}/phoneservices/${svc.id}/numbers`).pipe(
              catchError(() => of([])),
            ),
          }).pipe(
            map(({ user, numbers }) => ({
              userAccountId: svc.userAccountId,
              phoneServiceId: Number(svc.id),
              // "LastName, FirstName" per the header design; fall back to the id.
              userName: [user.lastName, user.firstName].filter(Boolean).join(', ') || `Account #${svc.userAccountId}`,
              // Active unless the account carries a known deactivated status. An
              // unknown/absent status (e.g. failed lookup) is treated as active so
              // we never show a false "inactive" badge. Convention mirrors
              // user-account-info + entity-selector.
              active: isUserAccountActive(user.status),
              numbers: numbers.map((n: any) => ({
                value: [n.numberBlock?.vorwahl, n.numberBlock?.prefix, n.suffix].filter(Boolean).join(''),
                type: (n.type as string || 'unknown').toLowerCase(),
              })),
            })),
          ),
        ));
      }),
      catchError(() => of([])),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((accounts) => {
      this.quickAccounts.set(accounts as QuickAccount[]);
      this.accountsLoading.set(false);
    });
  }

  private reloadAfterChange(): void {
    this.revisionsLoaded.set(false);
    this.revisionsLoadFailed.set(false);
    this.loadRevisionsIfNeeded();
    this.contractsChanged.emit();
  }

  private loadRevisionsIfNeeded(): void {
    if (this.revisionsLoaded() || this.loadingRevisions() || this.revisionsLoadFailed()) return;
    this.loadingRevisions.set(true);
    const brandId = this.session.brandId() ?? 1;
    // Old CMT resolves both maps in the *customer's* language
    // (contracts.dir.js:65-66 → customer.mainCustomerLanguage); 1 = German is the
    // fallback while no customer is loaded (EB-4143).
    const languageId = this.session.customerLanguage() ?? 1;
    forkJoin({
      revisions: this.contractsService.getRevisions(this.contract().id),
      featureNames: this.contractsService.getFeatureNames(languageId, brandId).pipe(catchError(() => of({}))),
      extensionNames: this.contractsService.getExtensionNames(languageId, brandId).pipe(catchError(() => of({}))),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ revisions, featureNames, extensionNames }) => {
        this.featureNames.set(featureNames as Record<string, string>);
        this.extensionNames.set(extensionNames as Record<string, string>);
        this.revisions.set(revisions);
        this.revisionsLoaded.set(true);
        this.loadingRevisions.set(false);
        this.resolveEmployeeNames(revisions);
        this.prefetchExtensionData();
      },
      error: () => {
        this.loadingRevisions.set(false);
        this.revisionsLoadFailed.set(true);
        // Drop what we were showing. After a mutation whose reload then failed, keeping it
        // would render the PRE-mutation list with no indication it is stale — the operator
        // would see the change they just made as absent (PR #42 re-review defect 1).
        this.revisions.set([]);
        this.toast.open('Failed to load revisions', 'OK', { duration: 5000 });
      },
    });
  }

  /**
   * Pre-warm the extension caches on expand so the Change-Discount, Add-Extension and
   * Remove-Extension dialogs open instantly (CMB-2953). The booked-extensions endpoint is the
   * slowest contract call (~1.5s); both catalogs are static (per price-list+tariff, and per plan).
   * Fire-and-forget — errors are swallowed here (the dialogs re-request and surface their own
   * errors). Re-runs via reloadAfterChange after any mutation, and the service invalidates the
   * booked-extensions cache on mutation so the re-warm fetches fresh data.
   *
   * Warmed for today's date only, which is what all three dialogs default to. Their
   * `getRevisionValidOn` call is deliberately not warmed: it is uncached, so a second
   * request would cost more than it saves (review run 2).
   */
  private prefetchExtensionData(): void {
    this.contractsService.getBookedExtensions(this.contract().id, todayIso()).pipe(
      catchError(() => of([])),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    const rev = this.activeRevision;
    if (rev?.priceListId && rev.tariff) {
      this.contractsService.getExtensionCatalog(rev.priceListId, rev.tariff).pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe();
    }
    // EB-4210 moved Add Extension off the price-list catalog onto the plan-scoped list,
    // which the warm above does not cover.
    if (rev?.planId) {
      this.contractsService.getExtensionsByPlan(rev.planId).pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe();
    }
  }

  private resolveEmployeeNames(revisions: ContractRevision[]): void {
    const allIds = new Set<number>();
    for (const r of revisions) {
      if (r.creatingEmployeeID !== null) allIds.add(r.creatingEmployeeID);
      if (r.changedBy !== null) allIds.add(r.changedBy);
    }
    if (allIds.size === 0) return;
    const lookups: Record<string, ReturnType<typeof this.contractsService.getEmployeeName>> = {};
    for (const id of allIds) {
      lookups[String(id)] = this.contractsService.getEmployeeName(id).pipe(catchError(() => of(`Employee #${id}`)));
    }
    forkJoin(lookups).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((nameMap) => {
      const updated = this.revisions().map(r => ({
        ...r,
        creatingEmployeeName: r.creatingEmployeeID !== null ? nameMap[String(r.creatingEmployeeID)] ?? r.creatingEmployeeName : r.creatingEmployeeName,
        changedByName: r.changedBy !== null ? nameMap[String(r.changedBy)] ?? r.changedByName : r.changedByName,
      }));
      this.revisions.set(updated);
    });
  }
}
